import { useEffect, useMemo, useRef, useState } from 'react';
import { getResourcesData, getAllCoursData } from '../services/contentService';
import PremiumAccessDialog from './PremiumAccessDialog';
import { submitResourceStateWithFallback } from '../services/progressSyncService';
import { readJsonArray, writeJson } from '../utils/storage';
import { generateHtmlFromChapitre } from '../utils/courseHtml';


const Resources = ({ onGoUpLevel, onGoBackTab, onOpenPremium }) => {
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);
  const [lockedTitle, setLockedTitle] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterLevel, setFilterLevel] = useState('all');
  const [favorites, setFavorites] = useState([]);
  const [readResources, setReadResources] = useState([]);
  const currentUserId = localStorage.getItem('concours_bf_current_user');
  const favoriteStorageKey = currentUserId
    ? `resource_favorites:${currentUserId}`
    : 'resource_favorites';
  const readStorageKey = currentUserId
    ? `resource_read:${currentUserId}`
    : 'resource_read';
  
  useEffect(() => {
    setFavorites(readJsonArray(favoriteStorageKey));
    setReadResources(readJsonArray(readStorageKey));
  }, [favoriteStorageKey, readStorageKey]);

  // Effet pour gérer le bouton Retour du header (niveaux de navigation)
  useEffect(() => {
    if (onGoUpLevel && onGoBackTab) {
      if (selectedResource) {
        // Niveau 2: Retour à la liste des ressources
        onGoUpLevel(() => {
          setSelectedResource(null);
        }, 2);
      } else if (selectedSubject) {
        // Niveau 1: Retour à la liste des matières
        onGoUpLevel(() => {
          setSelectedSubject(null);
        }, 1);
      }
    }
  }, [selectedSubject, selectedResource, onGoUpLevel, onGoBackTab]);

  const syncResourceState = (resourceId, updates) => {
    const numericId = Number(resourceId);
    if (!Number.isInteger(numericId) || numericId <= 0) {
      return;
    }

    submitResourceStateWithFallback(
      currentUserId,
      numericId,
      updates
    ).catch((error) => {
      console.warn(
        'Synchronisation de la ressource impossible; le cache local est conservé.',
        error
      );
    });
  };

  const toggleFavorite = (resourceId) => {
    const nextFavorite = !favorites.includes(resourceId);
    const newFavorites = nextFavorite
      ? [...favorites, resourceId]
      : favorites.filter((id) => id !== resourceId);

    setFavorites(newFavorites);
    writeJson(favoriteStorageKey, newFavorites);
    syncResourceState(resourceId, { is_favorite: nextFavorite });
  };

  const markAsRead = (resourceId) => {
    if (!readResources.includes(resourceId)) {
      const newRead = [...readResources, resourceId];
      setReadResources(newRead);
      writeJson(readStorageKey, newRead);
      syncResourceState(resourceId, { is_read: true });
    }
  };

  const [resourcesData, setResourcesData] = useState({});
  const [coursDataMapping, setCoursDataMapping] = useState({});
  const [loadingContent, setLoadingContent] = useState(true);
  const [contentError, setContentError] = useState(null);

  const detailOpenRef = useRef(false);
  detailOpenRef.current = Boolean(selectedResource);

  useEffect(() => {
    let isMounted = true;
    let requestVersion = 0;
    const load = () => {
      if (detailOpenRef.current) return;
      const version = ++requestVersion;
      setContentError(null);
      setLoadingContent(true);
      Promise.all([getResourcesData(), getAllCoursData()])
      .then(([resData, coursData]) => {
        if (isMounted && version === requestVersion) {
          setResourcesData(resData || {});
          setCoursDataMapping(coursData || {});
          setLoadingContent(false);
        }
      })
      .catch((err) => {
        if (isMounted && version === requestVersion) {
          setContentError(err.message || 'Erreur lors du chargement des ressources');
          setLoadingContent(false);
        }
      });
    };
    const visible = () => { if (document.visibilityState === 'visible') load(); };
    load();
    window.addEventListener('focus', load);
    document.addEventListener('visibilitychange', visible);
    return () => {
      isMounted = false;
      window.removeEventListener('focus', load);
      document.removeEventListener('visibilitychange', visible);
    };
  }, []);

  const subjects = useMemo(() => {
    return Object.entries(resourcesData).map(([key, data]) => {
      const newCoursData = coursDataMapping[key];
      let ressources = [...(data.ressources || [])];
      if (newCoursData) {
        // Support both the old single-course API and the new multi-course API.
        // Each course has its own Free/Premium entitlement; a locked course
        // must never expose chapters, including through the UI's HTML renderer.
        const courses = Array.isArray(newCoursData.courses)
          ? newCoursData.courses : [newCoursData];
        const multiCourse = courses.length > 1;
        // Legacy resource rows of type 'cours' are replaced by canonical
        // course chapters to avoid duplicate entries on the candidate side.
        ressources = ressources.filter((resource) => resource.type !== 'cours');
        courses.forEach((course) => {
          if (course.locked) {
            ressources.push({
              id: course.id ? `cours_${course.id}` : `${key}_cours_verrouille`,
              type: 'cours',
              titre: course.titre || course.matiere || 'Cours Premium',
              description: 'Accès Premium requis',
              duree: '',
              niveau: 'base',
              contenu: '',
              tags: [],
              access_tier: 'premium',
              locked: true,
            });
            return;
          }
          (course.chapitres || []).forEach((chapitre, idx) => {
            ressources.push({
              id: course.id ? `cours_${course.id}_chapitre_${idx}` : `${key}_chapitre_${idx}`,
              type: 'cours',
              titre: multiCourse && course.titre
                ? `${course.titre} · ${chapitre.titre}` : chapitre.titre,
              description: chapitre.objectif || `Chapitre de révision : ${chapitre.titre}`,
              duree: '20 min',
              niveau: 'base',
              contenu: generateHtmlFromChapitre(chapitre),
              tags: [key, 'révision', 'cours'],
              access_tier: course.access_tier || 'free',
              locked: false,
            });
          });
        });
      }

      return {
        key,
        ...data,
        ressources
      };
    });
  }, [resourcesData, coursDataMapping]);

  const getFilteredResources = (subjectKey) => {
    const subject = subjects.find(s => s.key === subjectKey);
    if (!subject) return [];
    
    let resources = subject.ressources || [];
    
    if (filterType !== 'all') {
      resources = resources.filter(r => r.type === filterType);
    }
    
    if (filterLevel !== 'all') {
      resources = resources.filter(r => r.niveau === filterLevel);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      resources = resources.filter(r => 
        r.titre.toLowerCase().includes(query) ||
        r.description.toLowerCase().includes(query) ||
        r.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }
    
    return resources;
  };

  const getTypeIcon = (type) => {
    const icons = {
      cours: '📖',
      fiche: '📝',
      document: '📄',
      pdf: '📑'
    };
    return icons[type] || '📚';
  };

  const getLevelColor = (level) => {
    const colors = {
      base: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      intermediaire: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      avance: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
    };
    return colors[level] || colors.base;
  };

  const getSubjectProgress = (subjectKey) => {
    const subject = subjects.find((item) => item.key === subjectKey);
    if (!subject || !subject.ressources) return { read: 0, total: 0, percent: 0 };
    
    const accessibleResources = subject.ressources.filter((r) => !r.locked);
    const total = accessibleResources.length;
    const read = accessibleResources.filter((r) =>
      readResources.includes(r.id)
    ).length;
    return { read, total, percent: total > 0 ? Math.round((read / total) * 100) : 0 };
  };

  const openResource = (resource) => {
    if (resource?.locked) {
      setLockedTitle(resource.titre || 'Cette ressource');
      return;
    }
    setSelectedResource(resource);
    markAsRead(resource.id);
  };

  const resourceTypes = [
    { value: 'all', label: 'Tous les types' },
    { value: 'cours', label: '📖 Cours' },
    { value: 'fiche', label: '📝 Fiches' },
    { value: 'document', label: '📄 Documents' },
    { value: 'pdf', label: '📑 PDF' }
  ];

  const levels = [
    { value: 'all', label: 'Tous les niveaux' },
    { value: 'base', label: 'Base' },
    { value: 'intermediaire', label: 'Intermédiaire' },
    { value: 'avance', label: 'Avancé' }
  ];

  if (selectedResource) {
    const progress = getSubjectProgress(selectedSubject);
    const isFavorite = favorites.includes(selectedResource.id);
    const subjectTitle = resourcesData[selectedSubject]?.titre || '';
    
    return (
      <div className="space-y-6 cb-resource-detail">
        <div className="flex items-center justify-between cb-resource-detail-actions">
          <div className="flex items-center gap-2">
            <button
              onClick={() => toggleFavorite(selectedResource.id)}
              className={`px-4 py-2 rounded-lg transition-colors ${
                isFavorite 
                  ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' 
                  : 'bg-gray-200 dark:bg-gray-700 hover:bg-yellow-100'
              }`}
            >
              {isFavorite ? '★ Favori' : '☆ Favori'}
            </button>
          </div>
        </div>

        <div className="surface-card rounded-2xl shadow-lg overflow-hidden cb-resource-reader">
          <div className="p-8 border-b border-color cb-resource-reader-head">
            <div className="flex items-center gap-4 mb-4">
              <span className="text-4xl">{getTypeIcon(selectedResource.type)}</span>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {selectedResource.titre}
                </h1>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getLevelColor(selectedResource.niveau)}`}>
                    {selectedResource.niveau}
                  </span>
                  <span className="text-secondary">
                    ⏱️ {selectedResource.duree}
                  </span>
                </div>
              </div>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-lg">
              {selectedResource.description}
            </p>
            <div className="flex flex-wrap gap-2 mt-4">
              {selectedResource.tags.map((tag, index) => (
                <span 
                  key={index}
                  className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full text-sm"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
          
          {selectedResource.type === 'pdf' && selectedResource.pdfUrl ? (
            <div className="p-8 bg-gray-100 dark:bg-slate-900">
              <iframe
                src={selectedResource.pdfUrl}
                title={selectedResource.titre}
                className="w-full h-[600px] border-0 rounded-lg bg-white dark:bg-white"
              />
              {!selectedResource.pdfUrl.startsWith('data:') && (
              <div className="mt-4 text-center">
                <a
                  href={selectedResource.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  📥 Ouvrir dans un nouvel onglet
                </a>
              </div>
              )}
            </div>
          ) : selectedResource.url && !selectedResource.contenu ? (
            <div className="p-8">
              <a href={selectedResource.url} target="_blank" rel="noopener noreferrer"
                className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                Ouvrir le document
              </a>
            </div>
          ) : (
            <div
              className="p-8 prose prose-slate dark:prose-invert max-w-none surface-card text-gray-900 dark:text-gray-100 cb-resource-reader-body"
              dangerouslySetInnerHTML={{ __html: selectedResource.contenu || '' }}
            />
          )}
        </div>

        <div className="surface-card p-6 rounded-2xl shadow-sm border border-color cb-resource-detail-progress">
          <h3 className="font-bold mb-4 text-gray-900 dark:text-white">📊 Progression de {subjectTitle}</h3>
          <div className="flex items-center gap-4">
            <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-3">
              <div 
                className="bg-blue-600 h-3 rounded-full transition-all duration-500"
                style={{ width: progress.percent + '%' }}
              />
            </div>
            <span className="font-semibold text-blue-600 dark:text-blue-400">
              {progress.read}/{progress.total} ({progress.percent}%)
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 cb-resources-page">
      {lockedTitle && <PremiumAccessDialog contentTitle={lockedTitle}
        onClose={() => setLockedTitle(null)}
        onUpgrade={() => { setLockedTitle(null); onOpenPremium?.(); }} />}
      <section className="cb-section-intro">
        <span className="cb-section-kicker">BIBLIOTHÈQUE DE RÉVISION</span>
        <h2>Ressources</h2>
        <p>Explorez les cours, fiches et documents disponibles par matière.</p>
      </section>

      {loadingContent && (
        <div className="flex items-center justify-center p-12 text-secondary">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
          Chargement des ressources...
        </div>
      )}

      {contentError && (
        <div className="p-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-2xl">
          ⚠️ {contentError}
        </div>
      )}

      <div className="surface-card p-6 rounded-2xl shadow-sm border border-color cb-resource-filter-panel">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <input
              type="text"
              placeholder="🔍 Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-3 bg-secondary rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
            />
          </div>
          
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-3 bg-secondary rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
          >
            {resourceTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
          
          <select
            value={filterLevel}
            onChange={(e) => setFilterLevel(e.target.value)}
            className="px-4 py-3 bg-secondary rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
          >
            {levels.map(level => (
              <option key={level.value} value={level.value}>{level.label}</option>
            ))}
          </select>
        </div>
      </div>

      {favorites.length > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-6 rounded-2xl border border-yellow-200 dark:border-yellow-800">
          <h3 className="text-xl font-bold text-yellow-800 dark:text-yellow-400 mb-4">⭐ Favoris</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {subjects.flatMap(subject => 
              (subject.ressources || [])
                .filter(r => favorites.includes(r.id) && !r.locked)
                .map(r => (
                  <button
                    key={r.id}
                    onClick={() => {
                      setSelectedSubject(subject.key);
                      openResource(r);
                    }}
                    className="p-4 surface-card rounded-xl shadow-sm border border-yellow-200 dark:border-yellow-800 hover:shadow-md transition-all text-left"
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">{getTypeIcon(r.type)}</span>
                      <div>
                        <h4 className="font-bold text-gray-900 dark:text-white">{r.titre}</h4>
                        <p className="text-sm text-secondary">{subject.titre}</p>
                      </div>
                    </div>
                  </button>
                ))
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 cb-resource-grid">
        {subjects.map((subject) => {
          const progress = getSubjectProgress(subject.key);
          const filteredResources = getFilteredResources(subject.key);
          const showSubject = selectedSubject === null || selectedSubject === subject.key;
          
          return (
            <div 
              key={subject.key}
              className={`surface-card rounded-2xl shadow-sm border border-color overflow-hidden transition-all cb-resource-subject-card ${
                !showSubject ? 'hidden' : ''
              }`}
            >
              <button
                onClick={() => setSelectedSubject(
                  selectedSubject === subject.key ? null : subject.key
                )}
                className="w-full p-6 flex items-center justify-between hover:bg-opacity-80 transition-all cb-resource-subject-trigger"
              >
                <div className="flex items-center gap-4">
                  <span className="text-4xl">{subject.icon}</span>
                  <div className="text-left">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      {subject.titre}
                    </h2>
                    <p className="text-sm text-secondary">
                      {subject.ressources?.length || 0} ressource{subject.ressources?.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                      {progress.percent}%
                    </span>
                    <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-1">
                      <div 
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: progress.percent + '%' }}
                      />
                    </div>
                  </div>
                </div>
              </button>

              {selectedSubject === subject.key && (
                <div className="border-t border-color">
                  {filteredResources.length > 0 ? (
                    <div className="divide-y divide-color cb-resource-list">
                      {filteredResources.map((resource) => {
                        const isFavorite = favorites.includes(resource.id);
                        const isRead = readResources.includes(resource.id);
                        
                        return (
                          <div key={resource.id}
                            className="w-full p-4 flex items-center gap-4 hover:bg-white dark:hover:bg-slate-700 transition-all cb-resource-row">
                            <button type="button"
                              onClick={() => openResource(resource)}
                              className="min-w-0 flex-1 flex items-center text-left hover:text-purple-700 focus-visible:outline-purple-600"
                            >
                            <div className="flex items-center gap-4">
                              <span className="text-2xl">{getTypeIcon(resource.type)}</span>
                              <div>
                                <div className="flex items-center gap-2">
                                  <h3 className="font-bold text-gray-900 dark:text-white">
                                    {resource.titre}
                                  </h3>
                                  {isRead && !resource.locked && (
                                    <span className="text-green-500" title="Lu">✓</span>
                                  )}
                                  {resource.locked && (
                                    <span className="text-xs font-semibold text-purple-600 dark:text-purple-400">
                                      🔒 Premium · Débloquer
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-secondary">
                                  {resource.description}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getLevelColor(resource.niveau)}`}>
                                    {resource.niveau}
                                  </span>
                                  <span className="text-xs text-gray-400">
                                    ⏱️ {resource.duree}
                                  </span>
                                </div>
                              </div>
                            </div>
                            </button>
                            {!resource.locked && (
                              <button type="button"
                                aria-label={isFavorite ? `Retirer ${resource.titre} des favoris` : `Ajouter ${resource.titre} aux favoris`}
                                onClick={() => toggleFavorite(resource.id)}
                                className={`p-2 rounded-lg transition-colors ${
                                  isFavorite
                                    ? 'text-yellow-500'
                                    : 'text-gray-300 hover:text-yellow-500'
                                }`}
                              >
                                {isFavorite ? '★' : '☆'}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-8 text-center text-secondary">
                      Aucune ressource ne correspond à vos critères
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {selectedSubject === null && (
        <div className="cb-catalog-hint">
          Sélectionnez une matière pour voir ses ressources.
        </div>
      )}
    </div>
  );
};

export default Resources;
